import { computed, inject, Injectable, signal } from '@angular/core';
import { NavigationItem } from '../models/NavigationItem.mode';
import { environment } from '@env/environment';
import { PermissionsService } from './permissions.service';
import {
  NAVIGATION_CONFIG,
  DIGITAL_HUB_TABS_CONFIG,
} from '../constants/navigation-items.const';
import { ToggleMode } from '../enums/toggle-mode.enum';
import { AppName } from '@shared/enums/app-name.enum';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private readonly permissionsService = inject(PermissionsService);

  private readonly isDigitalHub = environment.appName === AppName.AdminConsole;
  private readonly navigationConfig = NAVIGATION_CONFIG[environment.appName];
  private readonly filteredNavigationCache = new Map<
    string,
    NavigationItem[]
  >();

  activeToggle = signal<ToggleMode>(ToggleMode.Admin);

  readonly enableInstituteSwitcher = computed(() => {
    const config = DIGITAL_HUB_TABS_CONFIG.find(
      tab => tab.value === this.activeToggle()
    );

    return config?.enableInstituteSwitcher ?? true;
  });

  getNavigationItems(toggleMode?: ToggleMode): NavigationItem[] {
    let navigationItems: NavigationItem[];
    let cacheKey: string;

    if (this.isDigitalHub && toggleMode) {
      // Admin console: navigationConfig is an object with toggle mode keys
      navigationItems = (
        this.navigationConfig as Record<ToggleMode, NavigationItem[]>
      )[toggleMode];
      cacheKey = this.generateCacheKey(toggleMode);
    } else {
      // B2B portal: navigationConfig is directly a NavigationItem[]
      navigationItems = this.navigationConfig as NavigationItem[];
      cacheKey = this.generateCacheKey();
    }

    // Check cache first
    if (this.filteredNavigationCache.has(cacheKey)) {
      return this.filteredNavigationCache.get(cacheKey)!;
    }

    // Filter and cache the result
    const filteredItems = this.filterNavigationItems(navigationItems);
    this.filteredNavigationCache.set(cacheKey, filteredItems);

    return filteredItems;
  }

  private filterNavigationItems(items: NavigationItem[]): NavigationItem[] {
    return items
      .filter(item => item.enabled !== false && this.hasRequiredAccess(item))
      .map(item => ({
        ...item,
        subItems: item.subItems
          ? this.filterNavigationItems(item.subItems)
          : undefined,
      }));
  }

  private hasRequiredAccess(config: {
    requiredPrivileges?: { test: string[]; prod: string[] } | null;
  }): boolean {
    // If no privileges defined or all arrays empty → allow access
    if (
      !config.requiredPrivileges ||
      (!config.requiredPrivileges.test?.length &&
        !config.requiredPrivileges.prod?.length)
    ) {
      return true;
    }

    // Check if user has required privileges
    return this.permissionsService.checkPrivileges(config.requiredPrivileges);
  }

  private generateCacheKey(toggleMode?: ToggleMode): string {
    return toggleMode
      ? `${environment.appName}-${toggleMode}`
      : environment.appName;
  }

  clearCache(): void {
    this.filteredNavigationCache.clear();
  }

  getAllowedTabs(): typeof DIGITAL_HUB_TABS_CONFIG {
    return DIGITAL_HUB_TABS_CONFIG.filter(
      tab => tab.enableTab && this.hasRequiredAccess(tab)
    );
  }

  setActiveToggle(activeTab: ToggleMode): void {
    this.activeToggle.set(activeTab);
  }
}
